import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n/LanguageContext.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import api from '../services/api.js';
import { menuData } from './menuData.js';
import { filterNav, canAccessSlug } from '../config/access.js';
import InstallPrompt from '../components/InstallPrompt.jsx';
import IosInstallHint from '../components/IosInstallHint.jsx';
import BrandLogo from '../components/BrandLogo.jsx';
import '../styles/app-shell.css';
import LanguageToggle from '../components/LanguageToggle.jsx';
import HelpButton from '../components/help/HelpButton.jsx';
import { BRAND } from '../config/brand.js';

export default function AppShell() {
  const { t } = useTranslation();
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // In-app notification feed (bell). Connectors were previously excluded — the
  // bell was hardcoded "No notifications available." Load on mount, refresh on
  // route change and every 60s so referral status updates surface without a reload.
  const [inbox, setInbox] = useState({ unread: 0, items: [] });
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let active = true;
    const load = () => api.myNotifications().then((d) => { if (active && d) setInbox(d); }).catch(() => {});
    load();
    const timer = setInterval(load, 60_000);
    return () => { active = false; clearInterval(timer); };
  }, [isAuthenticated, location.pathname]);

  const openNotifications = () => {
    if (inbox.unread > 0) {
      api.markNotificationsRead()
        .then(() => setInbox((s) => ({ ...s, unread: 0, items: s.items.map((n) => ({ ...n, read: true })) })))
        .catch(() => {});
    }
  };

  // This list is hand-written rather than derived from menuData, so it has to
  // consult canAccessSlug itself — otherwise the New Referral FAB stayed on the
  // mobile bar for an unapproved connector even once the top nav hid the link
  // (QA 8180).
  const CONNECTOR_BOTTOM_NAV = [
    { slug: 'connector-dashboard', label: t('nav.home'),     icon: 'fa-solid fa-house',       fab: false },
    { slug: 'new-referral',        label: t('nav.referral'), icon: 'fa-solid fa-circle-plus', fab: true  },
    { slug: 'connector-profile',   label: t('nav.profile'),  icon: 'fa-solid fa-circle-user', fab: false },
  ].filter((item) => canAccessSlug(user, item.slug));
  const [collapsed, setCollapsed] = useState(true);

  const isConnector = user?.role === 'connector';
  // Connectors reach their profile from the avatar dropdown (and the mobile
  // bottom nav), so keep it out of the horizontal top-nav to reduce clutter.
  const navItems = filterNav(menuData.items, user).filter(
    (item) => !(isConnector && item.slug === 'connector-profile')
  );

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeNav = () => setCollapsed(true);

  const renderLeaf = (item, className) => (
    <NavLink
      to={`/${item.slug}`}
      className={({ isActive }) => `${className}${isActive ? ' active' : ''}`}
      onClick={closeNav}
    >
      {item.icon && <i className={`${item.icon} me-2`} aria-hidden="true" />}
      {item.labelKey ? t(item.labelKey) : item.label}
    </NavLink>
  );

  return (
    <>
      <InstallPrompt />
      <IosInstallHint />

      <nav className="navbar navbar-expand-lg navbar-dark bg-primary fixed-top shadow-sm">
        <div className="container-fluid">
          <Link className="navbar-brand" to="/">
            <BrandLogo size="sm" variant="light" />
          </Link>

          {/* Language toggle + notifications + user avatar: always visible outside
              collapse. order-lg-last pins them to the far-right corner on web while
              the nav links sit beside the brand; on mobile they stay next to it. */}
          <div className="d-flex align-items-center gap-2 ms-auto me-2 me-lg-0 order-lg-last">
            <HelpButton variant="light" />
            <LanguageToggle variant="light" />
            <div className="dropdown">
              <button
                type="button"
                className="btn btn-link position-relative text-white p-0 border-0"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                aria-label={t('nav.notifications')}
                onClick={openNotifications}
              >
                <i className="fa-solid fa-bell fs-5" aria-hidden="true" />
                {inbox.unread > 0 && (
                  <span
                    className="position-absolute translate-middle badge rounded-pill bg-danger"
                    style={{ top: 2, left: '100%', fontSize: 10 }}
                    data-testid="notif-count"
                  >
                    {inbox.unread > 9 ? '9+' : inbox.unread}
                  </span>
                )}
              </button>
              <div className="dropdown-menu dropdown-menu-end p-0 app-notif-menu">
                <h6 className="dropdown-header">Notifications</h6>
                {inbox.items.length === 0 ? (
                  <p className="text-muted small mb-0 px-3 py-2">No notifications available.</p>
                ) : (
                  <div className="app-notif-scroll">
                    {inbox.items.map((n) => (
                      <button
                        key={n.id}
                        type="button"
                        // A linkless notification is not tappable — it used to
                        // render as a live dropdown-item that silently did
                        // nothing on click (QA 7991).
                        className={`dropdown-item text-wrap py-2 border-top${n.read ? '' : ' bg-light'}${n.link ? '' : ' disabled'}`}
                        disabled={!n.link}
                        onClick={() => n.link && navigate(n.link)}
                      >
                        <div className="fw-semibold small text-dark">{n.title}</div>
                        {n.body && <div className="text-muted" style={{ fontSize: 12 }}>{n.body}</div>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {isAuthenticated ? (
              <div className="dropdown">
                <button
                  type="button"
                  className="btn btn-link text-white text-decoration-none d-flex align-items-center gap-2 p-0"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <span className="navbar-avatar d-flex align-items-center justify-content-center rounded-circle bg-white text-primary fw-bold" style={{ width: 32, height: 32, fontSize: 13 }}>
                    {(user?.name || user?.email || 'U')[0].toUpperCase()}
                  </span>
                  <span className="d-none d-lg-inline">
                    {user?.name || user?.email || 'Account'}
                  </span>
                  <i className="fa-solid fa-chevron-down small d-none d-lg-inline" aria-hidden="true" />
                </button>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li>
                    <span className="dropdown-item-text small text-muted">{user?.email}</span>
                  </li>
                  <li><hr className="dropdown-divider" /></li>
                  {isConnector && (
                    <li>
                      <NavLink className="dropdown-item" to="/connector-profile" onClick={closeNav}>
                        <i className="fa-solid fa-circle-user me-2" aria-hidden="true" />
                        {t('nav.profile')}
                      </NavLink>
                    </li>
                  )}
                  <li>
                    <button className="dropdown-item" type="button" onClick={handleLogout}>
                      <i className="fa-solid fa-right-from-bracket me-2" aria-hidden="true" />
                      {t('nav.logout')}
                    </button>
                  </li>
                </ul>
              </div>
            ) : (
              <Link className="btn btn-light btn-sm fw-semibold" to="/login">
                <i className="fa-solid fa-right-to-bracket me-2" aria-hidden="true" />
                Log in
              </Link>
            )}
          </div>

          {/* Hamburger — hidden entirely for connectors (QA 7983): on phones they
              navigate by the bottom nav, and navbar-expand-lg already expands the
              links at >=lg, so the button had nothing left to toggle. The previous
              `d-none d-lg-block` was inverted — it overrode Bootstrap's own
              >=lg hide and forced the button visible exactly where it was inert. */}
          <button
            className={`navbar-toggler${isConnector ? ' d-none' : ''}`}
            type="button"
            aria-controls="primaryNav"
            aria-expanded={!collapsed}
            aria-label="Toggle navigation"
            onClick={() => setCollapsed((c) => !c)}
          >
            <span className="navbar-toggler-icon" />
          </button>

          <div className={`collapse navbar-collapse${collapsed ? '' : ' show'}`} id="primaryNav">
            <ul className="navbar-nav me-auto mb-2 mb-lg-0">
              {navItems.map((item) =>
                item.children ? (
                  <li className="nav-item dropdown" key={item.label}>
                    <a
                      className="nav-link dropdown-toggle text-white"
                      href="#"
                      id={`nav-${item.label}`}
                      role="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      {item.icon && <i className={`${item.icon} me-2`} aria-hidden="true" />}
                      {item.label}
                    </a>
                    <ul className="dropdown-menu" aria-labelledby={`nav-${item.label}`}>
                      {item.children.map((child) => (
                        <li key={child.slug}>{renderLeaf(child, 'dropdown-item')}</li>
                      ))}
                    </ul>
                  </li>
                ) : (
                  <li className="nav-item" key={item.slug}>
                    {renderLeaf(item, 'nav-link text-white')}
                  </li>
                )
              )}
            </ul>
          </div>
        </div>
      </nav>

      <main className={`container-fluid py-4 px-3 px-lg-4${isConnector ? ' connector-main' : ''}`}>
        <Outlet />
      </main>

      {/* Bottom navigation — connector role, mobile only */}
      {isConnector && (
        <nav className="connector-bottom-nav d-lg-none" aria-label="Connector navigation">
          {CONNECTOR_BOTTOM_NAV.map((item) => (
            <NavLink
              key={item.slug}
              to={`/${item.slug}`}
              className={({ isActive }) =>
                `connector-bottom-nav__item${item.fab ? ' connector-bottom-nav__item--fab' : ''}${isActive ? ' active' : ''}`
              }
            >
              {item.fab ? (
                <span className="connector-bottom-nav__fab" aria-hidden="true">
                  <i className={item.icon} />
                </span>
              ) : (
                <>
                  <i className={`${item.icon} connector-bottom-nav__icon`} aria-hidden="true" />
                  <span className="connector-bottom-nav__label">{item.label}</span>
                  <span className="connector-bottom-nav__dot" aria-hidden="true" />
                </>
              )}
            </NavLink>
          ))}
        </nav>
      )}

      <footer className={`border-top py-4 mt-auto${isConnector ? ' d-none d-lg-block' : ''}`}>
        <div className="container-fluid text-center text-muted small">
          © {new Date().getFullYear()} {BRAND.legalName}. All rights reserved.
        </div>
      </footer>
    </>
  );
}
